import { FC, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { Asset, MsgExecute, MsgSend } from "rujira.js";
import { coins } from "rujira.js/src/signers/cosmos";
import {
  Button,
  Decimal,
  Fiat,
  i18n,
  IconDenom,
  Input,
  nFormatter,
  Numeric,
  useGlobalModalContext,
} from "rujira.ui";
import { ArrowRight, Split } from "rujira.ui/src/components/icons/Icons";
import { MsgProvider, TxButton } from "../../common/components/TxButton";
import { useAccounts } from "../../services/accounts";
import { useMsgAssetFragment } from "../../services/msg";
import { Subscription } from "../../services/useNodeSubscription";
import { GhostVaultBalanceFragment$key } from "./__generated__/GhostVaultBalanceFragment.graphql";
import { GhostVaultBalanceWithdrawFragment$key } from "./__generated__/GhostVaultBalanceWithdrawFragment.graphql";

const fragment = graphql`
  fragment GhostVaultBalanceFragment on GhostVaultAccount {
    id
    account
    receiptShares: shares {
      amount
      asset {
        ...msgAssetFragment
      }
    }
    receiptValue: value {
      amount
      asset {
        price {
          current
        }
        metadata {
          symbol
        }
      }
    }
    valueUsd
    ...GhostVaultBalanceWithdrawFragment
  }
`;

const subscription = graphql`
  subscription GhostVaultBalanceSubscription($id: ID!) {
    node(id: $id) {
      ... on GhostVaultAccount {
        id
        receiptShares: shares {
          amount
        }
        receiptValue: value {
          amount
        }
        valueUsd
      }
    }
  }
`;

export const GhostVaultBalance: FC<{
  symbol: string;
  k?: GhostVaultBalanceFragment$key;
}> = ({ symbol, k }) => {
  const account = useFragment(fragment, k);

  const { showModal, hideModal } = useGlobalModalContext();
  const shareAsset = useMsgAssetFragment(account?.receiptShares.asset);
  return (
    <div className="gradient-card gradient-card--purple h-full p-3 flex dir-c bg-black">
      {account && <Subscription id={account.id} subscription={subscription} />}
      <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">My Balance</i18n.h3>
      <Fiat
        symbol="$"
        amount={BigInt(account?.valueUsd || 0)}
        decimals={8}
        className="fs-22 fs-md-32 condensed fw-500 mt-1"
      />
      <Decimal
        symbol={` ${i18n.t("shares")}`}
        amount={BigInt(account?.receiptShares.amount || 0)}
        decimals={8}
        className="fs-16 fs-md-18 lh-16 lh-md-18 condensed mt-1"
        subscript
      />
      <div className="flex ai-c mt-2">
        <IconDenom denom={symbol} className="h-2 w-2 mr-1" />
        <Decimal
          amount={BigInt(account?.receiptValue.amount || 0)}
          round={4}
          className="fs-16 fs-md-18 lh-16 lh-md-18 condensed color-grey"
          subscript
        />
      </div>

      <Button
        disabled={!account || !account?.receiptShares.amount}
        onClick={() => {
          shareAsset &&
            showModal({
              //title: `Send ${symbol}`,
              backgroundClose: false,
              children: (
                <Send
                  asset={shareAsset}
                  symbol={shareAsset.metadata.symbol}
                  cancel={hideModal}
                  balance={BigInt(account?.receiptShares.amount || 0)}
                />
              ),
            });
        }}
        className="button--outline button--grey mt-3 w-full button--icon-right"
        label="Transfer">
        <ArrowRight />
      </Button>
      <Button
        disabled={!account || !account?.receiptShares.amount}
        onClick={() => {
          account &&
            showModal({
              //title: `Send ${symbol}`,
              backgroundClose: false,
              children: <Withdraw k={account} cancel={hideModal} />,
            });
        }}
        className="button--outline mt-1 w-full button--icon-right"
        label="Withdraw">
        <Split />
      </Button>
    </div>
  );
};

const Send: React.FC<{
  symbol: string;
  asset: Asset;
  balance: bigint;
  cancel: () => void;
}> = ({ asset, symbol, cancel, balance }) => {
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState(0n);
  const { selected } = useAccounts();

  const msg =
    address && amount && selected
      ? new MsgSend(
          { address: selected.address, network: "THOR" },
          asset,
          amount,
          address.trim()
        )
      : null;

  return (
    <MsgProvider msg={msg}>
      <h2 className="h3 flex ai-c">
        {i18n.t("Send")} {symbol}
        <IconDenom
          denom={asset.metadata.symbol}
          className="w-4 h-4 block ml-1"
        />
      </h2>

      <div className="text-right">
        <button
          className="tag tag--borderless px-0.5 pointer"
          onClick={() => setAmount(balance)}>
          {symbol} {i18n.t("Balance")}:{" "}
          <span className="color-white ml-0.5">
            {nFormatter(balance, 8, asset.metadata.decimals)}
          </span>
        </button>
      </div>

      <div className="row pad-mini mt-1.5 condensed fs-14 color-grey">
        <i18n.div className="col-8 fw-500">Rujira Address</i18n.div>
        <i18n.div className="col-4 text-right mr-0.5  fw-500">Amount</i18n.div>
      </div>
      <div className="row pad-mini mt-1">
        <div className="col-8 flex ai-c">
          <Input
            type="text"
            value={address}
            containerClassName="grow"
            onChange={(e) => {
              setAddress(e.currentTarget.value);
            }}
          />
        </div>
        <div className="col-4">
          <div className="flex ai-c">
            <Numeric
              full
              decimals={asset.metadata.decimals}
              amount={amount}
              onChangeAmount={setAmount}
            />
          </div>
        </div>
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            className="button"
            label="Send"
            onSuccess={() => cancel()}
          />
        </div>
      </div>
    </MsgProvider>
  );
};

const withdrawFragment = graphql`
  fragment GhostVaultBalanceWithdrawFragment on GhostVaultAccount {
    vault {
      address
    }
    receiptShares: shares {
      amount
      asset {
        ...msgAssetFragment
      }
    }
    receiptValue: value {
      amount
      asset {
        metadata {
          symbol
          decimals
        }
      }
    }
  }
`;

const Withdraw: React.FC<{
  k: GhostVaultBalanceWithdrawFragment$key;
  cancel: () => void;
}> = ({ k, cancel }) => {
  const [amount, setAmount] = useState(0n);
  const account = useFragment(withdrawFragment, k);
  const asset = useMsgAssetFragment(account.receiptShares.asset);
  const sharesBalance = BigInt(account.receiptShares.amount);
  const sharesValue = BigInt(account.receiptValue.amount);
  const { selected } = useAccounts();

  const withdrawalShares = useMemo(() => {
    return amount === sharesValue
      ? sharesBalance.toString()
      : ((amount * sharesBalance) / sharesValue).toString();
  }, [amount]);

  const msg = useMemo(
    () =>
      amount && asset.variants?.native?.denom && selected
        ? new MsgExecute(
            { address: selected.address, network: "THOR" },
            coins(withdrawalShares, asset.variants.native.denom),
            account.vault.address,
            { withdraw: {} }
          )
        : null,
    [amount, asset.variants?.native?.denom, account.vault.address]
  );

  return (
    <MsgProvider msg={msg}>
      <h2 className="h3 flex ai-c">
        {i18n.t("Withdraw")} {account.receiptValue.asset.metadata.symbol}
        <IconDenom
          denom={account.receiptValue.asset.metadata.symbol}
          className="w-4 h-4 block ml-1"
        />
      </h2>

      <div className="flex ai-c">
        <i18n.div className="condensed fs-14 color-grey fw-500 mt-0.5">
          Amount
        </i18n.div>
        <button
          className="tag tag--borderless px-0.5 pointer ml-a"
          onClick={() => setAmount(sharesValue)}>
          {account.receiptValue.asset.metadata.symbol} {i18n.t("Balance")}:{" "}
          <span className="color-white ml-0.5">
            {nFormatter(
              sharesValue,
              8,
              account.receiptValue.asset.metadata.decimals
            )}
          </span>
        </button>
      </div>

      <div className="mt-1">
        <Numeric
          full
          decimals={asset.metadata.decimals}
          amount={amount}
          onChangeAmount={setAmount}
        />
      </div>

      <div className="flex ai-c ml-a color-grey fs-12 condensed mt-1.5 pl-0.5">
        {asset.metadata.symbol} {i18n.t("Shares")}:{" "}
        <span className="color-white ml-0.5">
          {nFormatter(sharesBalance, 8, asset.metadata.decimals)}
        </span>
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s wrap">
        <Button
          className="button--grey button--outline mr-1"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            className="button"
            label="Withdraw"
            onSuccess={() => cancel()}
          />
        </div>
      </div>
    </MsgProvider>
  );
};
